
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { Download, Share2 } from 'lucide-react';
import Certificate from '../quiz/Certificate';
import { useAuth } from '@/contexts/AuthContext';

interface SkillTestAttempt {
  id: string;
  skill: string;
  created_at: string;
}

interface QuizResult {
  id: string;
  skill: string;
  score: number;
  total_questions: number;
  passed: boolean;
}

interface SkillTestResultsCardProps {
  quizAttempts: SkillTestAttempt[];
}

const SkillTestResultsCard: React.FC<SkillTestResultsCardProps> = ({ quizAttempts }) => {
  const [quizResults, setQuizResults] = useState<Record<string, QuizResult>>({});
  const [loading, setLoading] = useState(true);
  const { profile } = useAuth();
  
  useEffect(() => {
    const fetchQuizResults = async () => {
      if (quizAttempts.length === 0) {
        setLoading(false);
        return;
      }
      
      try {
        const { data, error } = await supabase
          .from('quiz_results')
          .select('id, skill, score, total_questions, passed')
          .in('skill', quizAttempts.map(attempt => attempt.skill));
          
        if (error) throw error;
        
        // Create a lookup object by skill
        const resultsMap: Record<string, QuizResult> = {};
        data?.forEach(result => {
          resultsMap[result.skill] = result;
        });
        
        setQuizResults(resultsMap);
      } catch (error) {
        console.error('Error fetching quiz results:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchQuizResults();
  }, [quizAttempts]);

  const calculateNextAttemptDate = (attemptDate: string) => {
    const date = new Date(attemptDate);
    const nextDate = new Date(date);
    nextDate.setDate(date.getDate() + 7);
    return nextDate.toLocaleDateString();
  };

  const handlePrintCertificate = (skill: string) => {
    const result = quizResults[skill];
    if (!result || !result.passed) return;
    
    const certificateElement = document.getElementById(`certificate-${skill}`);
    if (certificateElement) {
      const printWindow = window.open('', '', 'height=650,width=900');
      if (printWindow) {
        printWindow.document.write('<html><head><title>Certificate</title>');
        printWindow.document.write('<style>body { margin: 0; padding: 20px; }</style>');
        printWindow.document.write('</head><body>');
        printWindow.document.write(certificateElement.outerHTML);
        printWindow.document.write('</body></html>');
        printWindow.document.close();
        printWindow.focus();
        
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 500);
      }
    }
  };

  // Hide the card if there are no attempts
  if (quizAttempts.length === 0) {
    return null;
  }

  return (
    <Card className="border border-[#0EA5E9]/20 shadow-md bg-white/80">
      <CardHeader>
        <CardTitle className="text-xl">Your Skill Test Results</CardTitle>
        <CardDescription>
          View your performance in skill tests
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-6">
              <p className="text-gray-500">Loading test results...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {quizAttempts.map((attempt) => {
                const result = quizResults[attempt.skill];
                const hasPassed = result?.passed || false;
                
                return (
                  <Card key={attempt.id} className="bg-white border border-[#0EA5E9]/30 shadow-sm">
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <CardTitle className="text-lg">{attempt.skill}</CardTitle>
                        {hasPassed && (
                          <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
                            Passed
                          </Badge>
                        )}
                      </div>
                      <CardDescription>
                        Completed on {new Date(attempt.created_at).toLocaleDateString()}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-col gap-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Score:</span>
                          <span className={`font-semibold ${hasPassed ? 'text-green-600' : 'text-[#0EA5E9]'}`}>
                            {result ? 
                              `${result.score}%` : 
                              "Not available"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Next attempt:</span>
                          <span>
                            {calculateNextAttemptDate(attempt.created_at)}
                          </span>
                        </div>
                        
                        {hasPassed && (
                          <div className="mt-3 flex justify-end space-x-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="border-green-500 text-green-600 hover:bg-green-50"
                              onClick={() => handlePrintCertificate(attempt.skill)}
                            >
                              <Download className="h-4 w-4 mr-1" />
                              Certificate
                            </Button>
                            
                            <div style={{ display: 'none' }}>
                              <Certificate 
                                id={`certificate-${attempt.skill}`}
                                userName={`${profile?.first_name || ''} ${profile?.last_name || ''}`}
                                skill={attempt.skill}
                                score={result?.score || 0}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default SkillTestResultsCard;
